import type { TOC } from '@ember/component/template-only';
import { formatRating, joinList, releaseYear } from '../../utils/format';

interface Signature {
  Args: { data: Record<string, unknown> | null | undefined };
}

const VideoGameDetail: TOC<Signature> = <template>
  {{#let (asGameData @data) as |d|}}
    <div class="flex flex-col gap-3">
      <div class="flex flex-wrap gap-2">
        {{#if d.released}}
          <span class="badge badge-outline">{{releaseYear d.released}}</span>
        {{/if}}
        {{#each d.platforms as |p|}}
          <span class="badge badge-ghost badge-sm">{{p}}</span>
        {{/each}}
      </div>

      {{#if d.genres.length}}
        <div class="flex flex-wrap gap-1">
          {{#each d.genres as |genre|}}
            <span class="badge badge-outline badge-sm">{{genre}}</span>
          {{/each}}
        </div>
      {{/if}}

      {{#if (hasScores d)}}
        <div class="flex flex-wrap gap-3">
          {{#if d.rating}}
            <div class="flex items-center gap-1">
              <span class="text-warning font-bold">★</span>
              <span class="text-sm font-semibold">{{formatRating
                  d.rating
                }}</span>
              <span class="text-base-content/50 text-xs">RAWG</span>
            </div>
          {{/if}}
          {{#if d.metacritic}}
            <div class="flex items-center gap-1">
              <span class="text-sm font-semibold">{{d.metacritic}}</span>
              <span class="text-base-content/50 text-xs">Metacritic</span>
            </div>
          {{/if}}
        </div>
      {{/if}}

      {{#if d.developers.length}}
        <p class="text-base-content/60 text-xs">
          Dev:
          {{joinList d.developers}}
        </p>
      {{/if}}

      {{#if d.website}}
        <div>
          <a
            href={{d.website}}
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-xs btn-outline"
          >
            Official site ↗
          </a>
        </div>
      {{/if}}
    </div>
  {{/let}}
</template>;

interface GameData {
  name?: string;
  released?: string;
  rating?: number;
  metacritic?: number;
  platforms?: string[];
  genres?: string[];
  developers?: string[];
  publishers?: string[];
  backgroundImage?: string;
  website?: string;
}

function asGameData(raw: Record<string, unknown> | null | undefined): GameData {
  const d = (raw ?? {}) as GameData;
  d.platforms = d.platforms ?? [];
  d.genres = d.genres ?? [];
  d.developers = d.developers ?? [];
  return d;
}

function hasScores(d: GameData): boolean {
  return !!(d.rating ?? d.metacritic);
}

export default VideoGameDetail;
